import React from 'react'
import Image from 'next/image'
import Link from 'next/link'
import cn from 'classnames'

import { Tooltip } from '@components/TooltipCustom'
import { ProgressBar } from '@components/ProgressBar'
import { ArrowRight2Icon } from '@components/icons/ArrowRight2Icon'
import { InformationIcon } from '@components/icons/InformationIcon'
import { Avatar } from '@components/Avatar'
import { CharacterCardProps, CharacterInformationProps } from './interface'

function CharacterInformation({ content }: CharacterInformationProps) {
  return (
    <div className='font-light text-[12px] text-blue max-w-[288px]'>
      <div className='text-[10px] text-secondary dark:text-dark-10'>
        รายละเอียด
      </div>
      <div className='break-word'>{content || '-'}</div>
    </div>
  )
}

export function CharacterCard({
  id,
  chartRank,
  imageUrl,
  rank,
  nextRank,
  story,
  name,
  characterRole,
  bookId,
  bookName,
  coin,
  coinLeft,
  children,
  classNameImage,
  className,
  characterRankStartCoins,
  characterRankImg,
  size = 'm',
  canViewStory,
}: CharacterCardProps) {
  return (
    <div
      id='character-card'
      data-test='character-card'
      className={cn(
        'grid grid-cols-1 shadow-card bg-white gap-y-[10px] dark:bg-dark-8 rounded-[8px] border border-gray-4 dark:border-dark-1 w-full',
        {
          'p-[20px]': size === 'm',
          'p-[10px] mobile:p-[20px]': size === 's',
          'p-[10px] desktop:p-[20px]': size === 'xs',
        },
        className
      )}
    >
      <div className='relative'>
        <Avatar className='w-full h-full' src={imageUrl} alt={name} />

        <div className='absolute top-0 left-0 w-[30%] h-[30%]'>
          <Image
            src={characterRankImg}
            layout='fill'
            alt={`rank-${rank}`}
            sizes='(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw'
            quality={25}
            // width={size === 'm' ? '72' : '54'}
            // height={size === 'm' ? '72' : '54'}
          />
        </div>
      </div>
      <div className='flex justify-between'>
        {chartRank && (
          <div
            className={cn(
              'text-secondary dark:text-dark-10 font-mitr font-medium truncate break-word',
              {
                'text-[16px]': size === 'm',
                'text-[14px]': ['s', 'xs'].includes(size),
                'mobile:text-[16px]': size === 's',
                'desktop-tablet:text-[16px]': size === 'xs',
              }
            )}
          >
            อันดับที่ {chartRank}
          </div>
        )}
        <Tooltip label={<CharacterInformation content={story} />}>
          <InformationIcon className='text-blue-1' width='24' height='24' />
        </Tooltip>
      </div>
      {bookId && canViewStory ? (
        <Link href={`/story/${bookId}`} prefetch={false}>
          <a
            className={cn(
              'text-[14px] text-secondary-1 font-light line-clamp-1',
              {
                'text-[14px]': size === 'm',
                'text-[12px]': ['s', 'xs'].includes(size),
                'mobile:text-[14px]': size === 's',
                'desktop-tablet:text-[14px]': size === 'xs',
              }
            )}
          >
            {bookName}
          </a>
        </Link>
      ) : (
        <p
          className={cn(
            'text-[14px] text-secondary-1 font-light line-clamp-1',
            {
              'text-[14px]': size === 'm',
              'text-[12px]': ['s', 'xs'].includes(size),
              'mobile:text-[14px]': size === 's',
              'desktop-tablet:text-[14px]': size === 'xs',
            }
          )}
        >
          {bookName}
        </p>
      )}
      <div
        className={cn('flex justify-between space-x-[10px]', {
          'desktop:flex-col': size === 's',
          'items-center': size !== 's',
        })}
      >
        <div
          className={cn(
            'text-secondary dark:text-dark-10 font-mitr font-medium truncate break-word',
            {
              'text-[16px]': size === 'm',
              'text-[14px]': ['s', 'xs'].includes(size),
              'mobile:text-[16px]': size === 's',
              'desktop-tablet:text-[16px]': size === 'xs',
            }
          )}
        >
          {name}
        </div>
        <div
          className={cn('text-secondary-1 font-light whitespace-nowrap', {
            'text-[14px]': size === 'm',
            'text-[12px]': ['s', 'xs'].includes(size),
            'mobile:text-[14px]': size === 's',
            'desktop-tablet:text-[14px]': size === 'xs',
          })}
        >
          {characterRole}
        </div>
      </div>
      {nextRank ? (
        <div
          className={cn(
            'flex justify-between items-center text-secondary-1 font-light',
            {
              'text-[14px]': size === 'm',
              'text-[12px]': ['s', 'xs'].includes(size),
              'mobile:text-[14px]': size === 's',
              'desktop-tablet:text-[14px]': size === 'xs',
            }
          )}
        >
          <div>อีก {coinLeft.toLocaleString()} เหรียญ</div>

          <ArrowRight2Icon height='21' />

          <span
            className={cn('font-bold text-yellow uppercase', {
              'text-[16px]': size === 'm',
              'text-[14px]': ['s', 'xs'].includes(size),
              'mobile:text-[16px]': size === 's',
              'desktop-tablet:text-[16px] max-h-[21px]': size === 'xs',
            })}
          >
            {nextRank}
          </span>
        </div>
      ) : (
        <div
          className={cn('text-center text-[14px] text-secondary-1 font-light', {
            'text-[14px]': size === 'm',
            'text-[12px]': ['s', 'xs'].includes(size),
            'mobile:text-[14px]': size === 's',
            'desktop-tablet:text-[14px]': size === 'xs',
          })}
        >
          Rank :{' '}
          <span
            className={cn('font-bold text-yellow uppercase', {
              'text-[16px]': size === 'm',
              'text-[14px]': ['s', 'xs'].includes(size),
              'mobile:text-[16px]': size === 's',
              'desktop-tablet:text-[16px]': size === 'xs',
            })}
          >
            {rank}
          </span>
        </div>
      )}

      <ProgressBar
        progressBarClassName='bg-blue-1'
        value={coin - characterRankStartCoins}
        max={coinLeft + coin - characterRankStartCoins}
      />
      <div
        className={cn('text-secondary-1 font-light', {
          'text-[14px]': size === 'm',
          'text-[12px]': ['s', 'xs'].includes(size),
          'mobile:text-[14px]': size === 's',
          'desktop-tablet:text-[14px] max-h-[19px]': size === 'xs',
        })}
      >
        <span>ได้รับ {coin.toLocaleString()} เหรียญ</span>
      </div>
      {children}
    </div>
  )
}
